import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import TrainingModuleReader, { TYPE_META } from "@/components/TrainingModuleReader";
import { Tag, Clock, Eye, GraduationCap, CheckCircle2, Sparkles } from "lucide-react";

export default function PortalTraining() {
  const [items, setItems] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [reading, setReading] = useState(null);
  const [completedIds, setCompletedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [m, t, c] = await Promise.all([
        api.get("/training-modules", { params: { audience: "clinic" } }),
        api.get("/training-tracks").catch(() => ({ data: { clinic: [] } })),
        api.get("/training-completions").catch(() => ({ data: [] })),
      ]);
      setItems(m.data || []);
      setTracks((t.data && t.data.clinic) || []);
      setCompletedIds(new Set((c.data || []).map((x) => x.module_id)));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadAll();
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const m of items) {
      const k = m.track || "Other";
      if (!g[k]) g[k] = [];
      g[k].push(m);
    }
    for (const k of Object.keys(g)) g[k].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
    return g;
  }, [items]);

  const totalMinutes = items.reduce((s, m) => s + (m.duration_minutes || 0), 0);
  const completedCount = items.filter((m) => completedIds.has(m.id)).length;
  const progress = items.length ? Math.round((completedCount / items.length) * 100) : 0;
  const allDone = items.length > 0 && completedCount === items.length;

  const markComplete = async (mid) => {
    try {
      await api.post(`/training-modules/${mid}/complete`);
      setCompletedIds((s) => new Set([...s, mid]));
      toast.success("Marked complete.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const markIncomplete = async (mid) => {
    try {
      await api.delete(`/training-modules/${mid}/complete`);
      setCompletedIds((s) => {
        const next = new Set(s);
        next.delete(mid);
        return next;
      });
      toast.success("Marked incomplete.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <ToolPageShell
      eyebrow="Clinic · Training"
      title="Training & playbooks"
      intro="Get your team to expert in under an hour. Step-by-step modules curated by the Nodera ops team. Mark each complete as you go."
    >
      {!loading && items.length > 0 && (
        <>
          {/* Progress */}
          <div
            data-testid="portal-training-progress"
            className={`rounded-2xl border p-5 mb-6 transition-colors ${
              allDone
                ? "bg-emerald-50 border-emerald-200"
                : "bg-white border-slate-200"
            }`}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-emerald-700 inline-flex items-center gap-1.5">
                  {allDone ? (
                    <>
                      <Sparkles className="w-3.5 h-3.5" /> Library complete · 100%
                    </>
                  ) : (
                    <>Onboarding progress</>
                  )}
                </div>
                <h2 className="font-display text-xl font-bold text-slate-900 mt-0.5">
                  {completedCount} of {items.length} modules complete
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  {allDone
                    ? "Your team is fully onboarded. Time to deploy programs!"
                    : "Tap any card to open it, then mark it complete to track your team's onboarding."}
                </p>
              </div>
              <div className="text-right shrink-0">
                <div className={`font-display text-3xl font-extrabold tabular ${allDone ? "text-emerald-700" : "text-slate-900"}`}>
                  {progress}%
                </div>
              </div>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${allDone ? "bg-emerald-500" : "bg-slate-900"}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="portal-training-stats">
            <Stat label="Modules" value={items.length} icon={GraduationCap} />
            <Stat label="Completed" value={completedCount} icon={CheckCircle2} tone="emerald" />
            <Stat label="Tracks" value={Object.keys(grouped).length} icon={Tag} />
            <Stat label="Total minutes" value={totalMinutes} icon={Clock} />
          </div>
        </>
      )}

      {loading && <div className="text-sm text-slate-500">Loading training library…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="portal-training-empty">
          <GraduationCap className="w-7 h-7 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No published training modules yet.</p>
          <p className="text-xs text-slate-400 mt-1">Your concierge will publish your onboarding library shortly.</p>
        </div>
      )}

      {tracks.map(
        (track) =>
          grouped[track]?.length > 0 && (
            <section
              key={track}
              className="mb-8"
              data-testid={`portal-training-track-${track.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <h2 className="font-display text-base font-bold text-slate-900 mb-3 inline-flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-slate-400" /> {track}
                <span className="text-xs font-normal text-slate-400">
                  · {grouped[track].filter((m) => completedIds.has(m.id)).length}/{grouped[track].length}
                </span>
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {grouped[track].map((m) => (
                  <ModuleCard
                    key={m.id}
                    module={m}
                    completed={completedIds.has(m.id)}
                    onView={() => setReading(m)}
                  />
                ))}
              </div>
            </section>
          )
      )}
      {Object.keys(grouped)
        .filter((k) => !tracks.includes(k))
        .map((k) => (
          <section key={k} className="mb-8">
            <h2 className="font-display text-base font-bold text-slate-900 mb-3">{k}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {grouped[k].map((m) => (
                <ModuleCard
                  key={m.id}
                  module={m}
                  completed={completedIds.has(m.id)}
                  onView={() => setReading(m)}
                />
              ))}
            </div>
          </section>
        ))}

      {reading && (
        <TrainingModuleReader
          module={reading}
          completed={completedIds.has(reading.id)}
          onMarkComplete={() => markComplete(reading.id)}
          onMarkIncomplete={() => markIncomplete(reading.id)}
          onClose={() => setReading(null)}
        />
      )}
    </ToolPageShell>
  );
}

function Stat({ label, value, icon: Icon, tone }) {
  const toneCls =
    tone === "emerald"
      ? "bg-emerald-50 border-emerald-100 text-emerald-700"
      : "bg-slate-50 border-slate-200 text-slate-600";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
      <span className={`w-9 h-9 rounded-lg border flex items-center justify-center ${toneCls}`}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 truncate">{label}</div>
        <div className="font-display text-lg font-bold text-slate-900 tabular truncate">{value}</div>
      </div>
    </div>
  );
}

function ModuleCard({ module: m, completed, onView }) {
  const meta = TYPE_META[m.content_type] || TYPE_META.doc;
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={onView}
      data-testid={`portal-training-card-${m.id}`}
      className={`group text-left rounded-2xl border bg-white p-5 transition-colors ${
        completed ? "border-emerald-200" : "border-slate-200 hover:border-slate-400"
      }`}
    >
      <div className="flex items-start gap-2">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${meta.tone}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm font-semibold text-slate-900 leading-snug">{m.title}</div>
          <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1.5">
            {meta.label}
            {m.duration_minutes ? (
              <>
                <span>·</span>
                <Clock className="w-3 h-3" /> {m.duration_minutes} min
              </>
            ) : null}
          </div>
        </div>
        {completed && (
          <span className="shrink-0 inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.16em] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
            <CheckCircle2 className="w-3 h-3" /> Done
          </span>
        )}
      </div>
      {m.description && <p className="mt-3 text-xs text-slate-600 line-clamp-3">{m.description}</p>}
      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
        <span className="font-semibold text-blue-700 group-hover:text-blue-900 inline-flex items-center gap-1">
          <Eye className="w-3 h-3" /> Open module
        </span>
      </div>
    </button>
  );
}
